import { useContext, useMemo, useState } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import SmartSendItemInactive from './SmartSendItemInactive';
import SmartSendItemActive from './SmartSendItemActive';
import {
  ArrangeReturnCollectionAction,
  EditItemStatusAction,
  ReleaseItemsAction,
  RemoveTagAction,
  SaveItemsAction,
  SaveTagsAction,
  UpdateItemCacheAction
} from '../../../lib/context/SmartSendContextModels';
import SmartSendItemBooked from './SmartSendItemBooked';
import SmartSendItemProcessing from './SmartSendItemProcessing';
import SetMarketplaceAction, { ItemActionType } from './SetMarketplaceAction';
import SmartSendOrderProcessOrder from './SmartSendOrderProcessOrder';
import { IsReturnCollection, IsSmartSendByType } from '../../../lib/helpers/SmartSendSummaryGenerator';
import { EditType } from './EditType';
import SmartSendItemActiveMobile from './SmartSendItemActiveMobile';
import SmartSendItemBookedMobile from './SmartSendItemBookedMobile';
import SmartSendOrderProcessOrderMobile from './SmartSendOrderProcessOrderMobile';
import SmartSendItemInactiveMobile from './SmartSendItemInactiveMobile';
import SmartSendItemProcessingMobile from './SmartSendItemProcessingMobile';
import { useEventEmitter } from '../../../lib/em/Hooks';
import { Consts, EditEventType } from '../../../lib/em/EmitTypes';
import { EventSections } from '../../../lib/events/EventLogger';
import { CanArrangeReturn } from '../../../lib/helpers/CanArrangeReturn';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';

export interface SmartSendItemProps {
  item: SmartSendItemApiModel;
  isXs: boolean;
}

export default function SmartSendItemDisplay(props: SmartSendItemProps) {
  const ctx = useContext(SmartSendContext);
  const { emit } = useEventEmitter<EditEventType>();
  const { goTo } = useGoto();
  const { track } = useAnalytics();

  const display = useMemo(() => {
    const isXs = props.isXs ? 'Mobile' : 'Desktop';
    if (!IsSmartSendByType(props.item.Type)) {
      return `NonSmartSend_${isXs}`;
    }
    if (props.item.Status === 'Inactive') {
      return `Inactive_${isXs}`;
    }
    if (['AwaitingDispatch', 'Dispatched'].includes(props.item.MarketplaceStatus)) {
      return `Booked_${isXs}`;
    }
    if (props.item.BookingDetails?.BookingStatus === 'AwaitingPayment') {
      return `AwaitingPayment_${isXs}`;
    }
    return `Active_${isXs}`;
  }, []);

  const [isReturn] = useState(IsReturnCollection(props.item));
  const [canReturn] = useState(!isReturn && CanArrangeReturn(props.item));

  const logEvent = (events: string[]) => {
    if (display.startsWith('Active_') || display.startsWith('AwaitingPayment_')) {
      track({
        type: 'quick_actions_click',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.SmartSendItem, ...events]),
          action: events?.join(',')
        }
      });
    } else {
      track({
        type: 'quick_actions_click',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.BookedItems, EventSections.SmartSendItem]),
          action: events?.join(',')
        }
      });
    }
  };

  const toggleSelect = (val: boolean) => {
    ctx.dispatch(new UpdateItemCacheAction(props.item.Id, { ...props.item.State, selected: val }));
  };

  const removeTag = (tag: string) => {
    logEvent(['remove-tags']);
    ctx.dispatch(new RemoveTagAction(props.item.Id, tag));
  };

  const saveTags = (tags: string[]) => {
    logEvent(['save-tags']);
    ctx.dispatch(new SaveTagsAction([props.item.Id], tags));
  };

  const setEdit = (type: EditType) => {
    logEvent([type?.toLowerCase()]);
    emit(Consts.Edit, { Id: props.item.Id, Action: type });
  };

  const setAction = (type: ItemActionType) => {
    logEvent([type?.toLowerCase()]);
    SetMarketplaceAction(ctx.dispatch, props.item.Id, type);
  };

  const changeStatus = (status: string, select: boolean) => {
    logEvent([`status`, status]);
    ctx.dispatch(new EditItemStatusAction(props.item.Id, status, select));
  };

  const restore = (select: boolean) => {
    changeStatus('Active', select);
  };

  const saveForLater = (select: boolean) => {
    changeStatus('Inactive', select);
  };

  const deleteItem = () => {
    changeStatus('Deleted', false);
  };

  const arrangeReturn = async () => {
    ctx.dispatch(new ArrangeReturnCollectionAction([props.item.Id]));
    goTo(RoutingConstants.SmartSend);
  };

  const setDate = async (d: Date) => {
    logEvent(['set-date']);
    ctx.dispatch(new SaveItemsAction([props.item.Id], ['PreferredCollectionDate'], { PreferredCollectionDate: d }));
  };

  const release = () => {
    ctx.dispatch(new ReleaseItemsAction([props.item.Id]));
  };

  const book = () => {
    emit(Consts.Book, { Open: true, Ids: [props.item.Id] });
  };

  switch (display) {
    case 'NonSmartSend_Mobile':
      return (
        <SmartSendOrderProcessOrderMobile
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          isReturn={isReturn}
        />
      );
    case 'NonSmartSend_Desktop':
      return (
        <SmartSendOrderProcessOrder
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          isReturn={isReturn}
          arrangeReturn={canReturn ? arrangeReturn : null}
        />
      );
    case 'Inactive_Mobile':
      return (
        <SmartSendItemInactiveMobile
          key={props.item.Id}
          removeTag={removeTag}
          saveTags={saveTags}
          item={props.item}
          toggleSelect={toggleSelect}
          restore={restore}
          deleteItem={deleteItem}
          isReturn={isReturn}
        />
      );
    case 'Inactive_Desktop':
      return (
        <SmartSendItemInactive
          key={props.item.Id}
          removeTag={removeTag}
          saveTags={saveTags}
          item={props.item}
          toggleSelect={toggleSelect}
          restore={restore}
          delete={deleteItem}
          isReturn={isReturn}
        />
      );
    case 'Booked_Mobile':
      return (
        <SmartSendItemBookedMobile
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          removeTag={removeTag}
          saveTags={saveTags}
          action={setAction}
          isReturn={isReturn}
        />
      );
    case 'Booked_Desktop':
      return (
        <SmartSendItemBooked
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          removeTag={removeTag}
          saveTags={saveTags}
          action={setAction}
          isReturn={isReturn}
          arrangeReturn={canReturn ? arrangeReturn : null}
        />
      );
    case 'AwaitingPayment_Mobile':
      return (
        <SmartSendItemProcessingMobile key={props.item.Id} item={props.item} cancel={release} isReturn={isReturn} />
      );
    case 'AwaitingPayment_Desktop':
      return <SmartSendItemProcessing key={props.item.Id} item={props.item} cancel={release} isReturn={isReturn} />;
    case 'Active_Mobile':
      return (
        <SmartSendItemActiveMobile
          key={props.item.Id}
          item={props.item}
          toggleSelect={toggleSelect}
          edit={setEdit}
          removeTag={removeTag}
          saveTags={saveTags}
          action={setAction}
          isReturn={isReturn}
          saveForLater={saveForLater}
          deleteItem={deleteItem}
          book={book}
        />
      );
    case 'Active_Desktop':
      return (
        <SmartSendItemActive
          key={props.item.Id}
          item={props.item}
          setDate={setDate}
          toggleSelect={toggleSelect}
          edit={setEdit}
          removeTag={removeTag}
          saveTags={saveTags}
          action={setAction}
          saveForLater={saveForLater}
          deleteItem={deleteItem}
          book={book}
          isReturn={isReturn}
        />
      );
    default:
      return null;
  }
}
